import {
    chain, filter, get, includes, isArray, isObject, map, orderBy, size, toLower, uniqBy,
} from 'lodash';

import {
    useCallback, useContext, useEffect, useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { UserRoles } from '../../../constants/UserRole';
import EMPLOYER_STATUSES from '../../../constants/EmployerStatuses';
import SettingsContext from '../../../contexts/SettingsContext';
import AuthorizationContext from '../../../contexts/AuthorizationContext';
import OfficeHttpService from '../../../api/OfficeHttpService';
import EmployerHttpService from '../../../api/EmployerHttpService';
import EmployerRepHttpService from '../../../api/EmployerRepHttpService';
import RecruiterHttpService from '../../../api/RecruiterHttpService';
import Logger from '../../../services/logger';
import EmployerContext from '../../../contexts/EmployerContext';
import { useMessageEditor } from './useMessageEditor';

const checkIsActiveEmployer = (officeEmployer) => get(officeEmployer, 'status.current') === EMPLOYER_STATUSES.ACTIVE;

const useMenuData = (currentUserInfo, employerNameSearchValue) => {
    const [offices, setOffices] = useState([]);
    const [employers, setEmployers] = useState([]);
    const [recruiters, setRecruiters] = useState([]);

    const { user, employerRep } = useContext(AuthorizationContext);

    const fetchData = useCallback(async () => {
        try {
            if (currentUserInfo.isTalenyaView || currentUserInfo.isTalenyaUser) {
                const [
                    fetchedRecruiters,
                    fetchedEmployers,
                    fetchedOffices,
                ] = await Promise.all([
                    RecruiterHttpService.getAllRecruitersForMenu(),
                    EmployerHttpService.getAllEmployersForMenu(),
                    OfficeHttpService.getAllOffices(),
                ]);

                setOffices(fetchedOffices);
                setEmployers(fetchedEmployers);
                setRecruiters(fetchedRecruiters);

                return;
            }

            if (currentUserInfo.isRecruiterUser) {
                const fetchedRecruiters = await RecruiterHttpService.getAllRecruitersForMenu();
                const currentUserRecruiter = (fetchedRecruiters || []).find((recruiter) => recruiter._id === currentUserInfo.recruiterId) || {};
                const positionsIds = (currentUserRecruiter.positions || []).map(({ positionId }) => positionId);

                setRecruiters(fetchedRecruiters);

                const [fetchedEmployers, fetchedOffices] = await Promise.all([
                    EmployerHttpService.getEmployersByPositionIds(positionsIds),
                    OfficeHttpService.getAllOffices(),
                ]);

                setEmployers(fetchedEmployers);
                setOffices(fetchedOffices);
            }

            const multiUser = employerRep?.multiUser;
            const isUserResellerEmployerRep = currentUserInfo.resellerEmployerId;
            const isUserAssociatedEmployerRep = currentUserInfo.isAssociatedEmployer;
            if ((multiUser || isUserResellerEmployerRep || isUserAssociatedEmployerRep) && currentUserInfo.isEmployerRepAdmin) {
                const userEmail = user.email;
                let employerReps = await EmployerRepHttpService.getEmployerRepsByEmail(userEmail, true, true) || [];
                if (!isArray(employerReps) && isObject(employerReps)) {
                    employerReps = [employerReps];
                }
                if (employerReps?.length) {
                    const employerIDs = map(employerReps, 'employerId');
                    const [
                        fetchedEmployers,
                        fetchedOffices,
                    ] = await Promise.all([
                        EmployerHttpService.searchEmployers({
                            employerIDs,
                            projection: ['name', 'office', 'status.current', 'settings.isResellerPartner'],
                        }),
                        OfficeHttpService.getAllOffices(),
                    ]);
                    const resellerEmployerIds = chain(fetchedEmployers)
                        .filter((employer) => employer?.settings?.isResellerPartner)
                        .map('_id')
                        .value();
                    if (size(resellerEmployerIds)) {
                        const associatedEmployers = await EmployerHttpService.searchEmployers({
                            resellerEmployerId: resellerEmployerIds,
                            projection: ['name', 'office', 'status.current', 'settings.isResellerPartner'],
                        });
                        fetchedEmployers.push(...associatedEmployers);
                    }
                    setOffices(fetchedOffices);
                    setEmployers(uniqBy(fetchedEmployers, '_id'));
                }
            }
        } catch (error) {
            Logger.error(error);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, []);

    return {
        offices,
        employers: employerNameSearchValue
            ? filter(employers, (e) => includes(toLower(e.name), toLower(employerNameSearchValue)))
            : employers,
        recruiters,
    };
};

// Modified by AI on 05/14/2026. Edit #2.
const useUserInfo = () => {
    const { user, isEmployerRepAdmin } = useContext(AuthorizationContext);

    if (!user?.impersonation) {
        return {
            isTalenyaUser: false,
            isRecruiterUser: false,
            isEmployerRepUser: false,
            isEmployerRepAdmin: false,
            isTalenyaView: false,
            isRecruiterView: false,
            isEmployerView: false,
            recruiterId: null,
            employerId: null,
            resellerEmployerId: null,
            isAssociatedEmployer: false,
        };
    }

    const { realEntityId, impersonatedEntityId } = user.impersonation;

    const isTalenyaUser = user.impersonation.realEntityRole === UserRoles.talenya;
    const isRecruiterUser = user.impersonation.realEntityRole === UserRoles.recruiter;
    const isEmployerRepUser = user.impersonation.realEntityRole === UserRoles.employerRep;

    const isTalenyaView = isTalenyaUser && !user.impersonation.impersonatedAsEmployer && !user.impersonation.impersonatedAsRecruiter;
    const isRecruiterView = isRecruiterUser || user.impersonation.impersonatedAsRecruiter;
    const isEmployerView = isEmployerRepUser || user.impersonation.impersonatedAsEmployer;

    const recruiterId = (isRecruiterUser || isTalenyaUser) ? realEntityId : impersonatedEntityId;
    const employerId = isEmployerRepUser ? user.employerId : impersonatedEntityId;

    const resellerEmployerId = user.origUserPrefs?.resellerEmployerId;

    const isAssociatedEmployer = user.origUserPrefs?.isAssociatedEmployer;

    return {
        isTalenyaUser,
        isRecruiterUser,
        isEmployerRepUser,
        isEmployerRepAdmin,
        isTalenyaView,
        isRecruiterView,
        isEmployerView,
        recruiterId,
        employerId,
        resellerEmployerId,
        isAssociatedEmployer,
    };
};

export const getUserInfo = useUserInfo;

const getPreparedRecruiters = ({ recruiters, recruiterId }) => {
    if (!recruiters?.length) {
        return [];
    }

    const filteredAndSorted = (recruiters || [])
        .filter((recruiter) => get(recruiter, 'fullName', '').trim() && recruiter._id !== recruiterId)
        .sort((recruiter1, recruiter2) => (recruiter1.fullName > recruiter2.fullName ? 1 : -1));
    const currentUserRecruiter = (recruiters || []).find((recruiter) => recruiter._id === recruiterId);

    if (!currentUserRecruiter) {
        return filteredAndSorted;
    }

    return [currentUserRecruiter, ...filteredAndSorted];
};

const getPreparedEmployers = ({ employers, offices }) => (offices || []).map((office) => {
    const filteredEmployers = chain(employers || [])
        .filter((employer) => office === employer.office)
        .orderBy(['settings.isResellerPartner', 'name'], ['desc', 'asc'])
        .value();
    return ({
        name: office,
        employers: filteredEmployers,
    });
});

export const useMenuItems = ({
    isEmployerFilterApplied,
    employerNameSearchValue,
}) => {
    const history = useHistory();
    const showMessageEditor = useMessageEditor();

    const { user } = useContext(AuthorizationContext);
    const { environmentSettings: { TFTAppUrl }, systemPreferences: { usersAllowedToEditEmailTemplates = [] } } = useContext(SettingsContext);
    const { employer } = useContext(EmployerContext);

    const isEditEmailTemplatesMenuItemVisible = usersAllowedToEditEmailTemplates.includes(user?.email);

    const currentUserInfo = useUserInfo();

    const {
        isTalenyaUser,
        isRecruiterUser,
        isTalenyaView,
        isRecruiterView,
        isEmployerView,
        recruiterId,
        resellerEmployerId,
        isEmployerRepAdmin,
        isAssociatedEmployer,
    } = currentUserInfo;

    const {
        offices,
        employers,
        recruiters,
    } = useMenuData(currentUserInfo, employerNameSearchValue);

    const isUserResellerEmployerRep = Boolean(resellerEmployerId);
    const isUserAssociatedEmployerRep = Boolean(isAssociatedEmployer);

    const menuItems = [];

    if (isTalenyaUser) {
        menuItems.push({
            label: 'DASHBOARD',
            testId: 'DASHBOARD',
            onClick: () => {
                window.location.href = `${TFTAppUrl}/#/talenya/jobs`;
            },
        });
    }

    if (isTalenyaUser || isRecruiterUser
        || ((isUserResellerEmployerRep
        || (isUserAssociatedEmployerRep && employers.length > 1))
        && isEmployerRepAdmin)
    ) {
        const employersByOffice = getPreparedEmployers({
            offices,
            employers: isEmployerFilterApplied
                ? filter(employers, checkIsActiveEmployer)
                : employers,
        });

        menuItems.push({
            label: 'EMPLOYERS',
            testId: 'EMPLOYERS',
            onClick: () => {},
            hasActiveFilter: true,
            subItems: (employersByOffice || [])
                .filter((office) => office.employers.length > 0)
                .map(({ name, employers: officeEmployers = [] }) => ({
                    label: name,
                    testId: `employer-${name}`,
                    onClick: () => {},
                    subItems: orderBy(officeEmployers, ['settings.isResellerPartner', 'name'], ['desc', 'asc'])
                        .map((officeEmployer) => ({
                            label: officeEmployer.name,
                            testId: `employer-${name}-${officeEmployer.name}`,
                            onClick: () => {
                                history.push(`/employer/${officeEmployer._id}/positions`);
                            },
                            isResellerPartner: officeEmployer?.settings?.isResellerPartner,
                        })),
                })),
        });
    }

    if (isTalenyaUser) {
        const preparedRecruiters = getPreparedRecruiters({ recruiters, recruiterId });

        menuItems.push({
            label: 'TALENT_EXPERTS',
            testId: 'TALENT_EXPERTS',
            onClick: () => {},
            subItems: (preparedRecruiters || []).map((recruiter) => ({
                label: recruiter.fullName,
                testId: `recruiter-${recruiter.fullName}`,
                onClick: () => {
                    window.location.href = `${TFTAppUrl}/#/recruiter/${recruiter._id}/positions`;
                },
            })),
        });
    }

    menuItems.push({
        label: 'JOBS',
        testId: 'JOBS',
        onClick: () => {
            if (isTalenyaView) {
                window.location.href = `${TFTAppUrl}/#/talenya/jobs`;
            }

            if (isRecruiterView) {
                window.location.href = `${TFTAppUrl}/#/recruiter/${recruiterId}/positions`;
            }

            if (isEmployerView) {
                history.push(`/employer/${employer._id}/positions`);
            }
        },
    });

    if (isTalenyaView || isRecruiterView) {
        menuItems.push({
            label: 'ADD_CAND',
            testId: 'ADD_CAND',
            onClick: () => {
                window.location.href = `${TFTAppUrl}/#/recruiter//createNewCandidate`;
            },
        });
    }

    if (isEditEmailTemplatesMenuItemVisible) {
        menuItems.push({
            label: 'EDITOR',
            testId: 'EDITOR',
            onClick: () => {
                showMessageEditor();
            },
        });
    }

    return { menuItems };
};
